import type { ChimeTone } from './chime';
import {
  isUrgentNotification,
  type NotificationKind,
  type StaffNotification,
} from '../services/notificationService';

/**
 * When the bell rings — the rules, without any audio or tab concerns.
 *
 * - Urgent kinds ring at once. Those where a customer is actively waiting
 *   repeat every 30 s (five plays in total) until the entry is read or
 *   resolved, or the staff opened that chat/call. "Agent musste einspringen"
 *   rings once: nobody waits any more, the agent answered. So does a blocked
 *   WhatsApp number: the state changed, no chat is waiting.
 * - Quiet kinds (customer writes with the bot, running call) ring the soft
 *   chime at most once per contact/call and minute.
 * - An entry that did not change (replay after a reconnect, same upsert via
 *   refetch) never rings twice; one that grew ("3 neue Nachrichten") rings
 *   again and restarts its repeats.
 *
 * Every chime carries a key that is identical in every tab — the tab
 * coordinator uses it to let exactly one tab play it.
 */

export const URGENT_REPEAT_MS = 30_000;
/** Plays per alert including the first one: now, +30 s, +60 s, +90 s, +120 s. */
export const URGENT_MAX_PLAYS = 5;
export const SOFT_THROTTLE_MS = 60_000;
/** Older than this on arrival = history (replay after a long disconnect), not news. */
export const MAX_ALERT_AGE_MS = 10 * 60_000;

interface ChimePolicy {
  tone: ChimeTone;
  repeat: boolean;
}

const CHIME_POLICY: Record<NotificationKind, ChimePolicy> = {
  handoff_requested: { tone: 'urgent', repeat: true },
  customer_waiting: { tone: 'urgent', repeat: true },
  handoff_unanswered: { tone: 'urgent', repeat: false },
  callback_requested: { tone: 'urgent', repeat: true },
  callback_window_closing: { tone: 'urgent', repeat: true },
  // A state change, not a customer waiting: once is the alarm, the bell keeps it until read.
  whatsapp_channel_blocked: { tone: 'urgent', repeat: false },
  whatsapp_activity: { tone: 'soft', repeat: false },
  call_live: { tone: 'soft', repeat: false },
  call_transfer: { tone: 'soft', repeat: false },
};

export function chimePolicy(item: Pick<StaffNotification, 'kind' | 'urgent'>): ChimePolicy {
  return (
    CHIME_POLICY[item.kind] ??
    (isUrgentNotification(item) ? { tone: 'urgent', repeat: false } : { tone: 'soft', repeat: false })
  );
}

export interface ChimeRequest {
  tone: ChimeTone;
  /** Same in every tab for the same play — the cross-tab dedup key. */
  key: string;
  /** First play of this alert (desktop notification only then, not on repeats). */
  first: boolean;
  notification: StaffNotification | null;
  /** Soft chimes: at most once per group and window across ALL tabs, not just this one. */
  throttle?: { group: string; windowMs: number };
}

export interface OpenTarget {
  contactKey?: string | null;
  callSid?: string | null;
}

export interface ChimeScheduler {
  /** Remember the initial list without ringing. */
  prime: (items: StaffNotification[]) => void;
  /** A live upsert, or an entry seen in a refetch. */
  observe: (item: StaffNotification, source: 'live' | 'snapshot') => void;
  markRead: (ids: number[] | 'all') => void;
  /**
   * A read was rolled back (request failed): resume the repeats of entries a
   * customer is still waiting on — next chime after the usual 30 s, not at once.
   */
  rearm: (items: StaffNotification[]) => void;
  /** The staff has this chat/call open (null = none): no repeats for it. */
  noteOpened: (target: OpenTarget | null) => void;
  /** One urgent chime at app start (open callback requests). */
  ringStartup: (reason: string) => void;
  reset: () => void;
  destroy: () => void;
}

export interface ChimeSchedulerOptions {
  onChime: (request: ChimeRequest) => void;
  /** Server-calibrated clock (event times are the server's). */
  now: () => number;
}

interface Alarm {
  item: StaffNotification;
  signature: string;
  plays: number;
  timer: ReturnType<typeof setTimeout> | null;
}

const signatureOf = (item: StaffNotification) => `${item.updated_at}|${item.count}`;

function matchesTarget(item: StaffNotification, target: OpenTarget | null): boolean {
  if (!target) return false;
  if (target.contactKey && item.contact_key === target.contactKey) return true;
  return Boolean(target.callSid && item.call_sid === target.callSid);
}

function throttleKey(item: StaffNotification): string {
  if (item.contact_key) return `contact:${item.contact_key}`;
  if (item.call_sid) return `call:${item.call_sid}`;
  return `notification:${item.id}`;
}

export function createChimeScheduler({ onChime, now }: ChimeSchedulerOptions): ChimeScheduler {
  const seen = new Map<number, string>();
  const alarms = new Map<number, Alarm>();
  const lastSoft = new Map<string, number>();
  let openTarget: OpenTarget | null = null;

  const stopAlarm = (id: number) => {
    const alarm = alarms.get(id);
    if (!alarm) return;
    if (alarm.timer) clearTimeout(alarm.timer);
    alarms.delete(id);
  };

  const stopAll = () => {
    Array.from(alarms.keys()).forEach(stopAlarm);
  };

  const playAlarm = (alarm: Alarm) => {
    alarm.plays += 1;
    onChime({
      tone: 'urgent',
      key: `urgent:${alarm.item.id}:${alarm.signature}:${alarm.plays}`,
      first: alarm.plays === 1,
      notification: alarm.item,
    });
  };

  const scheduleRepeat = (alarm: Alarm) => {
    if (alarm.plays >= URGENT_MAX_PLAYS) {
      alarms.delete(alarm.item.id);
      return;
    }
    alarm.timer = setTimeout(() => {
      alarm.timer = null;
      if (alarms.get(alarm.item.id) !== alarm) return;
      playAlarm(alarm);
      scheduleRepeat(alarm);
    }, URGENT_REPEAT_MS);
  };

  const startAlarm = (item: StaffNotification, signature: string, repeat: boolean) => {
    stopAlarm(item.id);
    const alarm: Alarm = { item, signature, plays: 0, timer: null };
    playAlarm(alarm);
    if (!repeat || matchesTarget(item, openTarget)) return;
    alarms.set(item.id, alarm);
    scheduleRepeat(alarm);
  };

  const ringSoft = (item: StaffNotification, signature: string) => {
    const key = throttleKey(item);
    const t = now();
    const last = lastSoft.get(key);
    if (last !== undefined && t - last < SOFT_THROTTLE_MS) return;
    lastSoft.set(key, t);
    onChime({
      tone: 'soft',
      key: `soft:${item.id}:${signature}`,
      first: true,
      notification: item,
      throttle: { group: `soft:${key}`, windowMs: SOFT_THROTTLE_MS },
    });
  };

  const isFresh = (item: StaffNotification) => {
    const t = Date.parse(item.updated_at);
    return Number.isNaN(t) || now() - t <= MAX_ALERT_AGE_MS;
  };

  const observe = (item: StaffNotification) => {
    const signature = signatureOf(item);
    const known = seen.get(item.id);
    seen.set(item.id, signature);
    if (item.read_at || item.resolved_at) {
      stopAlarm(item.id);
      return;
    }
    if (known === signature || !isFresh(item)) return;
    const policy = chimePolicy(item);
    if (policy.tone === 'urgent') startAlarm(item, signature, policy.repeat);
    else ringSoft(item, signature);
  };

  return {
    prime: (items) => {
      items.forEach((item) => seen.set(item.id, signatureOf(item)));
    },
    observe: (item) => observe(item),
    markRead: (ids) => {
      if (ids === 'all') stopAll();
      else ids.forEach(stopAlarm);
    },
    rearm: (items) => {
      items.forEach((item) => {
        if (item.read_at || item.resolved_at || alarms.has(item.id) || !isFresh(item)) return;
        const policy = chimePolicy(item);
        if (policy.tone !== 'urgent' || !policy.repeat || matchesTarget(item, openTarget)) return;
        const alarm: Alarm = { item, signature: `${signatureOf(item)}|rearm`, plays: 1, timer: null };
        alarms.set(item.id, alarm);
        scheduleRepeat(alarm);
      });
    },
    noteOpened: (target) => {
      openTarget = target && (target.contactKey || target.callSid) ? target : null;
      alarms.forEach((alarm, id) => {
        if (matchesTarget(alarm.item, openTarget)) stopAlarm(id);
      });
    },
    ringStartup: (reason) => {
      onChime({ tone: 'urgent', key: `startup:${reason}`, first: true, notification: null });
    },
    reset: () => {
      stopAll();
      seen.clear();
      lastSoft.clear();
      openTarget = null;
    },
    destroy: () => {
      stopAll();
    },
  };
}
